import { useEffect, useMemo } from "react";
import { lastFocusRecord } from "components/pages/Educator/Dashboard/assignmentDraft";

// Per-class memory of the last lesson/standard the teacher worked on, so the
// create flows keep their default even after the class's bundles are gone.
// Archiving deletes the classroom assignment, not the curriculum lesson or the
// standard, so a stored record stays valid indefinitely.
const keyFor = (classroomId) => `okoLastFocus:${classroomId}`;

const read = (classroomId) => {
  if (!classroomId) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(classroomId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const write = (classroomId, record) => {
  try {
    window.localStorage.setItem(keyFor(classroomId), JSON.stringify(record));
  } catch (e) {
    // Private mode / quota — the live derive still works, only the memory is lost.
  }
};

// The later of a stored and a live record. An undated live record loses to a
// dated stored one; when neither is dated there's nothing to compare, so the
// live derive wins and the memory behaves exactly as it did before it existed.
const newer = (storedRecord, liveRecord) => {
  if (!storedRecord) return liveRecord || null;
  if (!liveRecord) return storedRecord;
  return (liveRecord.updatedAt || "") >= (storedRecord.updatedAt || "") ? liveRecord : storedRecord;
};

/**
 * The class's last focus — the memory only ever moves forward.
 *
 * Archiving removes bundles, so the derive it leaves behind is older than what's
 * stored and is ignored: the memory keeps pointing at the lesson/standard that
 * was genuinely worked on last. Real work carries a fresher `last_updated_at`
 * and advances it.
 *
 * Only a non-null derive is written. Without that guard the no-lessons empty
 * state — which mounts with an empty list and is the main reader — would clobber
 * the record before anything could read it.
 */
export const useLastFocus = (classroomId, bundles) => {
  const live = useMemo(() => lastFocusRecord(bundles), [bundles]);
  // Read every render rather than memoized: it has to see what the effect below
  // wrote, or an archive's older derive would look like the only record and move
  // the memory backwards. A parse of one small object is cheaper than that bug.
  const stored = read(classroomId);
  const focus = newer(stored, live);
  const serialized = focus ? JSON.stringify(focus) : null;

  useEffect(() => {
    if (classroomId && serialized) write(classroomId, JSON.parse(serialized));
  }, [classroomId, serialized]);

  return focus;
};
